import type { TWidgetPackageJson } from '@lucimark/shared/models/widgetPackageJson'
import { widgetPackageJsonSchema } from '@lucimark/shared/models/widgetPackageJson'

/**
 * Browser-safe helpers for widget .tgz bundles (SPA or single-file).
 * Used by app (preview) and player (Service Worker virtual file server).
 */

/**
 * Guess MIME type from filename extension for widget bundle files.
 */
export function guessMimeForWidgetFile(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  const mime: Record<string, string> = {
    js: 'application/javascript',
    mjs: 'application/javascript',
    css: 'text/css',
    html: 'text/html',
    json: 'application/json',
    svg: 'image/svg+xml',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    ico: 'image/x-icon',
    woff: 'font/woff',
    woff2: 'font/woff2',
    ttf: 'font/ttf',
    map: 'application/json'
  }
  return mime[ext] ?? 'application/octet-stream'
}

/** Tar typeflag: regular file ('0' = 0x30, '\0' = 0, '7' = 0x37 contiguous). */
const TAR_TYPEFLAG_REGULAR = new Set([0, 0x30, 0x37])

/**
 * Parse a tar buffer (uncompressed) into a map of path -> file bytes.
 * Only regular files (typeFlag '0', '\0', or '7') are included; directories and symlinks are skipped.
 * Paths are normalized: backslashes to slashes, leading ./ removed.
 * Size is parsed as octal; invalid/NaN is treated as 0 so parsing continues (avoids empty result from one bad header).
 *
 * Returns 0 files when:
 * - Input is still gzip-compressed (must decompress before calling parseTar).
 * - First header has empty name (not tar or corrupted).
 * - Every entry has typeFlag not in {0, 0x30, 0x37} (e.g. directories only) or size 0.
 * Use getTarFirstHeaders(decompressed, 5) to inspect first headers when debugging.
 */
export function parseTar(buffer: Uint8Array): Record<string, Uint8Array> {
  const files: Record<string, Uint8Array> = {}
  const decoder = new TextDecoder('utf-8')
  let offset = 0

  while (offset + 512 <= buffer.length) {
    const header = buffer.slice(offset, offset + 512)
    const name = decoder.decode(header.slice(0, 100)).replace(/\0/g, '').trim()
    const typeFlag: number = header[156] ?? 0
    if (!name) { break }

    const sizeStr = decoder.decode(header.slice(124, 136)).trim() || '0'
    const size: number = Number.parseInt(sizeStr, 8) || 0

    if (TAR_TYPEFLAG_REGULAR.has(typeFlag) && size > 0) {
      const normalizedName = name.replace(/\\/g, '/').replace(/^\.\//, '')
      files[normalizedName] = buffer.slice(offset + 512, offset + 512 + size)
    }

    offset += 512 + Math.ceil(size / 512) * 512
  }

  return files
}

/**
 * Read first N tar headers for diagnostics (name, typeFlag, size). Use when parseTar returns empty
 * to see why (e.g. wrong typeflag, size 0/NaN, or blob not tar).
 */
export function getTarFirstHeaders(buffer: Uint8Array, maxHeaders: number = 5): { name: string, typeFlag: number, size: number }[] {
  const decoder = new TextDecoder('utf-8')
  const out: { name: string, typeFlag: number, size: number }[] = []
  let offset = 0
  for (let i = 0; i < maxHeaders && offset + 512 <= buffer.length; i++) {
    const header = buffer.slice(offset, offset + 512)
    const name = decoder.decode(header.slice(0, 100)).replace(/\0/g, '').trim()
    if (!name) { break }
    const typeFlag: number = header[156] ?? 0
    const sizeStr = decoder.decode(header.slice(124, 136)).trim() || '0'
    const size: number = Number.parseInt(sizeStr, 8) || 0
    out.push({ name, typeFlag, size })
    offset += 512 + Math.ceil(size / 512) * 512
  }
  return out
}

/**
 * Build a tar buffer from a list of (path, file bytes). One 512-byte header + content (+ padding) per entry,
 * then two 512-byte zero blocks. Use for repacking widget bundles without Node stream APIs.
 */
export function packTar(entries: Array<[string, Uint8Array]>): Uint8Array {
  const encoder = new TextEncoder()
  const blocks: Uint8Array[] = []
  for (const [path, data] of entries) {
    const nameBytes = encoder.encode(path)
    const size = data.length
    const sizeOctal = size.toString(8).slice(-12).padStart(12, '0')
    const header = new Uint8Array(512)
    header.fill(0)
    header.set(nameBytes.subarray(0, 100), 0)
    header.set(encoder.encode('0000644\0'), 100)
    header.set(encoder.encode('0000000\0'), 108)
    header.set(encoder.encode('0000000\0'), 116)
    header.set(encoder.encode(sizeOctal), 124)
    header.set(encoder.encode('00000000000\0'), 136)
    header.set(encoder.encode('        '), 148)
    header[156] = 0x30
    header.set(encoder.encode('ustar\0'), 257)
    header.set(encoder.encode('00'), 263)
    let sum = 0
    for (let i = 0; i < 512; i++) { sum += header[i]! }
    const chksumStr = `${(sum).toString(8).slice(-7).padStart(7, '0')}\0`
    header.set(encoder.encode(chksumStr).subarray(0, 8), 148)
    blocks.push(header, data)
    const pad = (512 - (size % 512)) % 512
    if (pad > 0) { blocks.push(new Uint8Array(pad)) }
  }
  blocks.push(new Uint8Array(1024))
  const total = blocks.reduce((a, b) => a + b.byteLength, 0)
  const out = new Uint8Array(total)
  let off = 0
  for (const b of blocks) {
    out.set(b, off)
    off += b.byteLength
  }
  return out
}

export type WidgetBundleFileMap = Record<string, { bytes: Uint8Array, mime: string }>

/** Skip macOS AppleDouble/resource-fork entries (._*) so they are never served or chosen as index. */
function isAppleDoublePath(path: string): boolean {
  return path.split('/').some(segment => segment.startsWith('._'))
}

/** Required root-level files in a widget .tgz (package.json and index.html at bundle root). */
export const WIDGET_BUNDLE_REQUIRED_ROOT_FILES = ['package.json', 'index.html'] as const

/** Max tar entries allowed to mitigate DoS via huge archives. */
export const WIDGET_BUNDLE_MAX_ENTRIES = 2000

/**
 * Normalize a tar entry path for comparison (backslashes to slashes, strip leading ./).
 */
export function normalizeWidgetEntryPath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\.\//, '').trim()
}

/**
 * True if the path is safe (no path traversal, no absolute path, no NUL).
 * Use after normalizeWidgetEntryPath for upload validation.
 */
export function isSafeWidgetEntryPath(normalizedPath: string): boolean {
  if (normalizedPath.includes('\0')) { return false }
  if (normalizedPath.startsWith('/')) { return false }
  const parts = normalizedPath.split('/').filter(Boolean)
  return !parts.includes('..')
}

/**
 * Check that the widget bundle has required files at the archive root (no top-level folder
 * like dist/ wrapping everything). Used at upload time in app and api.
 * Also rejects path traversal, absolute paths, and excessive entry count (DoS mitigation).
 * Entry paths should be normalized (e.g. via normalizeWidgetEntryPath).
 */
export function requireWidgetBundleRootStructure(entryPaths: string[]): { ok: true } | { ok: false, message: string } {
  if (entryPaths.length > WIDGET_BUNDLE_MAX_ENTRIES) {
    return {
      ok: false,
      message: `Invalid widget bundle: too many entries (max ${WIDGET_BUNDLE_MAX_ENTRIES}). The archive may be malformed or too large.`
    }
  }
  const normalized = entryPaths.map(normalizeWidgetEntryPath).filter(p => !isAppleDoublePath(p) && !p.startsWith('PaxHeader/'))
  for (const p of normalized) {
    if (!isSafeWidgetEntryPath(p)) {
      return {
        ok: false,
        message: 'Invalid widget bundle: archive contains invalid or unsafe paths (e.g. path traversal). Use only normal relative paths at the bundle root.'
      }
    }
  }
  const atRoot = (filename: string) => normalized.includes(filename)
  for (const file of WIDGET_BUNDLE_REQUIRED_ROOT_FILES) {
    if (!atRoot(file)) {
      return {
        ok: false,
        message: `Invalid widget bundle: "${file}" must be at the root of the archive (e.g. include package.json and index.html at root, not inside a folder like dist/). Repack with: tar czf widget.tgz package.json -C dist .`
      }
    }
  }
  return { ok: true }
}

/**
 * True if the relative path (no leading slash, no "..") is safe to serve under scopePrefix.
 */
function isSafeRelativePath(relative: string): boolean {
  if (relative.startsWith('/')) { return false }
  const parts = relative.split('/').filter(Boolean)
  return !parts.includes('..')
}

/**
 * Build a virtual file map from tar entries: each key is scopePrefix + normalized path,
 * value is { bytes, mime }. Preserves full path (e.g. "dist/assets/foo.js" stays under "dist/")
 * so the Service Worker can serve requests like /w/<pathId>/dist/assets/foo.js.
 * Skips macOS ._* metadata files and any entry that would resolve outside the virtual root (security).
 */
export function buildWidgetBundleFileMap(
  tarFiles: Record<string, Uint8Array>,
  scopePrefix: string,
  guessMime: (filename: string) => string = guessMimeForWidgetFile
): WidgetBundleFileMap {
  const fileMap: WidgetBundleFileMap = {}
  for (const [name, bytes] of Object.entries(tarFiles)) {
    if (isAppleDoublePath(name)) { continue }
    if (!isSafeRelativePath(name)) { continue }
    const vPath = `${scopePrefix}${name}`
    const filename = name.split('/').pop() ?? name
    fileMap[vPath] = { bytes, mime: guessMime(filename) }
  }
  return fileMap
}

/**
 * Parse package.json from a widget tar file map (e.g. from parseTar).
 * Returns null if package.json is missing or invalid.
 */
export function getWidgetPackageJsonFromTarFiles(tarFiles: Record<string, Uint8Array>): TWidgetPackageJson | null {
  const key = Object.keys(tarFiles).find(
    p => normalizeWidgetEntryPath(p) === 'package.json' && !isAppleDoublePath(p)
  )
  if (!key) { return null }
  const bytes = tarFiles[key]
  if (!bytes?.length) { return null }
  try {
    const raw = new TextDecoder('utf-8').decode(bytes).replace(/^\uFEFF/, '').trim()
    if (!raw.startsWith('{')) { return null }
    const parsed = JSON.parse(raw) as unknown
    return widgetPackageJsonSchema.parse(parsed)
  }
  catch {
    return null
  }
}
